import { Typography } from "@mui/material";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowUpDown } from "lucide-react";

import { RoleCell } from "./RoleCell";
import DeleteDialog from "../DeleteDialog";
import DialogSpecificPassword from "../DialogSpecificPassword";
import { DialogSubsEdit } from "../DialogSubsEdit";
import { Subscriptions } from "@/redux/clients/types/Clients.interface";
import { useIsMobile } from "@/hooks/use-mobile";

export type UserColumns = {
  id: string;
  username: string;
  role: string;
  createdBy: string;
  email: string;
  position: string;
  company: string;
  suscripciones: Subscriptions[];
};

export function useUsersColumns(): ColumnDef<UserColumns>[] {
  const isMobile = useIsMobile();
  const columns: ColumnDef<UserColumns>[] = [
    !isMobile && {
      accessorKey: "username",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Usuario
          </Typography>
        </div>
      ),
      cell: ({ row: { original: user } }: { row: Row<UserColumns> }) => {
        const username = user.username;

        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="nombre-usuario"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {username}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: ({ column }: { column: Column<UserColumns> }) => {
        return (
          <Button
            variant="ghost"
            className="pl-0 ml-0 hover:bg-gray-200"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
              Correo
            </Typography>
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row: { original: user } }: { row: Row<UserColumns> }) => {
        const email = user.email;

        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="correo-usuario"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {email}
            </Typography>
          </div>
        );
      },
      enableHiding: false,
    },
    !isMobile && {
      accessorKey: "createdBy",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Creado por
          </Typography>
        </div>
      ),
      cell: ({ row: { original: user } }: { row: Row<UserColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="creado-por"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {user.createdBy}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "role",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Rol
          </Typography>
        </div>
      ),
      cell: ({ row: { original: user } }: { row: Row<UserColumns> }) => {
        return (
          <div className="text-left font-medium" aria-label="rol-usuario">
            <RoleCell user={user} />
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row: { original: user } }: { row: Row<UserColumns> }) => {
        return (
          <div
            className="flex items-center gap-2"
            aria-label="acciones-usuario"
          >
            <DeleteDialog userId={user.id} />
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 w-8 p-0 hover:bg-gray-200 rounded-full"
                >
                  <span className="sr-only">Abrir menú</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="flex flex-col">
                <DropdownMenuLabel>
                  <Typography
                    fontFamily={"Inter"}
                    fontSize={"12px"}
                    fontWeight={600}
                  >
                    Acciones
                  </Typography>
                </DropdownMenuLabel>
                <DialogSubsEdit userInfo={user} userId={user.id} />
                <DialogSpecificPassword userId={user.id} />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      enableHiding: false,
    },
  ].filter(Boolean) as ColumnDef<UserColumns>[];
  return columns;
}
